/**
 * The pocket's text fields (settings pocket spec §4–§5): a labelled 36px
 * capsule for forms (Git, Dev server, Add folder, Password) and an in-place
 * value at a row's end. Saving is useSavedTextField's job.
 */
import { useId, type ComponentProps } from "react";
import { TextInput } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { SavedTextField } from "./settings-draft-state";

/** The inline error under a field. */
export function SettingsFieldError({
  error,
  testId,
  id,
}: {
  error: string | null;
  testId: string;
  /** Lets the field point at its error with aria-describedby. */
  id?: string;
}) {
  if (!error) return null;
  return (
    <p
      id={id}
      role="alert"
      className="px-2 pt-1 text-appkit-small font-medium text-danger"
      data-testid={testId}
    >
      {error}
    </p>
  );
}

export function SettingsTextField({
  label,
  error = null,
  mono = false,
  testId,
  className,
  ...inputProps
}: {
  label: string;
  error?: string | null;
  mono?: boolean;
  testId: string;
} & Omit<ComponentProps<"input">, "size">) {
  const id = useId();
  return (
    <div className="px-2 py-1.5">
      <label
        htmlFor={id}
        className="mb-1 block text-appkit-small font-medium text-muted"
      >
        {label}
      </label>
      <TextInput
        {...inputProps}
        id={id}
        shape="capsule"
        className={cn(mono && "font-mono text-appkit-caption", className)}
        data-testid={testId}
      />
      {error ? (
        <p
          role="alert"
          className="pt-1 text-appkit-small font-medium text-danger"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function SettingsInlineText({
  field,
  ariaLabel,
  placeholder,
  testId,
}: {
  field: SavedTextField;
  ariaLabel: string;
  placeholder?: string;
  testId: string;
}) {
  return (
    <TextInput
      {...field.inputProps}
      shape="capsule"
      aria-label={ariaLabel}
      placeholder={placeholder}
      className="w-[170px] text-right focus:text-left"
      data-testid={testId}
    />
  );
}
